import { createRoot, type Root } from "react-dom/client";
import type { Channel } from "@/api_client";
import { registerClientMiddlewareNoHook } from "@/open_api/RegisterClientMiddleware";
import { agpIdToChannelMap } from "@/web_analytics/dataLayer/events/data/agpId";
import initAppInsights from "./appInsights";
import { initLazyGtm } from "./gtmLazyLoader";
import ReactWrapper from "./reactWrapper";

class ProductShadowBase extends HTMLElement {
  protected shadow: ShadowRoot;
  protected backendHost: string | null | undefined;
  protected appPathPrefix: string | null | undefined;
  protected cssAssetPath: string = "/assets/webshop-fi-components.css";
  protected appInsightsConnectionString: string | undefined;
  protected skipCart: boolean = false;
  protected recaptchaSiteKey: string | null | undefined;
  protected gtmContainerId: string | null | undefined;
  // Factory for producing children; stored to allow re-rendering on attribute changes
  private childrenFactory?: () => React.ReactNode;
  // Cache a single React root to avoid duplicate mounts
  private reactRoot?: Root;
  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  // Observe attribute changes that we care about post-initialization
  static get observedAttributes() {
    return ["channel", "language"];
  }

  // Public property accessors for channel (maps to the 'channel' attribute)
  get channel(): Channel | null {
    return (this.getAttribute("channel") as Channel) ?? null;
  }

  set channel(value: string | null) {
    if (value === null || value === undefined) {
      this.removeAttribute("channel");
    } else {
      this.setAttribute("channel", value);
    }
  }

  get language(): string | null {
    return this.getAttribute("language") ?? null;
  }

  set language(value: string | null) {
    if (value === null || value === undefined) {
      this.removeAttribute("language");
    } else {
      this.setAttribute("language", value);
    }
  }

  // Hook for subclasses to respond to seller id changes
  protected onChannelChanged(
    _oldValue: string | null,
    _newValue: string | null,
  ) {
    // Intentionally empty: subclasses can override.
  }

  // Hook for subclasses to respond to language changes
  protected onLanguageChanged(
    _oldValue: string | null,
    _newValue: string | null,
  ) {
    // Intentionally empty: subclasses can override.
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (name === "channel" && oldValue !== newValue) {
      this.onChannelChanged(oldValue, newValue);
      this.renderComponent();
    }

    if (name === "language" && oldValue !== newValue) {
      this.onLanguageChanged(oldValue, newValue);
      this.renderComponent();
    }
  }

  loadAndRender(getChildren: () => React.ReactNode) {
    // Store for potential re-renders on attribute changes
    this.childrenFactory = getChildren;
    this.appInsightsConnectionString =
      this.getAttribute("app-insights-connection-string") ?? undefined;
    this.backendHost = this.getAttribute("backend-host");
    this.appPathPrefix = this.getAttribute("app-path-prefix");
    this.cssAssetPath = this.getAttribute("css-path") ?? this.cssAssetPath;
    this.recaptchaSiteKey = this.getAttribute("recaptcha-site-key");
    this.gtmContainerId = this.getAttribute("gtm-container-id");
    this.skipCart = this.getAttribute("skip-cart") === "true";

    registerClientMiddlewareNoHook(this.backendHost ?? undefined, true);
    // Derive seller-id from agpId query parameter if not already explicitly set.
    try {
      const url = new URL(globalThis.location.href);
      const agpIdParam = url.searchParams.get("agpId");
      if (agpIdParam && !this.getAttribute("channel")) {
        const parsed = Number(agpIdParam);
        if (!Number.isNaN(parsed) && parsed in agpIdToChannelMap) {
          const mappedSeller = agpIdToChannelMap[parsed];
          if (mappedSeller) {
            this.setAttribute("channel", mappedSeller);
          }
        }
      }
    } catch {
      // Silently ignore URL parsing issues (unlikely in browser environment)
    }

    initAppInsights(this.appInsightsConnectionString);
    if (this.gtmContainerId) {
      initLazyGtm(this.gtmContainerId, 6000);
    }

    this.loadResources(this.backendHost, this.recaptchaSiteKey)
      .then(() => {
        return this.renderComponent();
      })
      .catch((err) => {
        console.error(err);
      });
  }

  async loadResources(
    backendHost: string | null,
    recaptchaSiteKey?: string | null,
  ) {
    const linkElements = [];
    if (backendHost && !this.cssAssetPath.startsWith("http")) {
      linkElements.push({
        href: `${backendHost}${this.cssAssetPath}`,
      });
    } else {
      linkElements.push({
        href: this.cssAssetPath,
      });
    }

    const resourcePromises = linkElements.map(
      ({ href }) =>
        new Promise<void>((resolve, reject) => {
          const linkElement = document.createElement("link");
          linkElement.setAttribute("rel", "stylesheet");
          linkElement.setAttribute("href", href);

          linkElement.onload = () => {
            resolve();
          };

          linkElement.onerror = () => {
            const message = `Failed to load stylesheet: ${href}`;
            reject(new Error(message));
          };

          this.shadow.appendChild(linkElement);
        }),
    );

    if (recaptchaSiteKey) {
      const recaptchaPromise = new Promise<void>((resolve, reject) => {
        globalThis.recaptchaSiteKey = recaptchaSiteKey;

        const recaptchaScriptElement = document.createElement("script");
        recaptchaScriptElement.setAttribute(
          "src",
          `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`,
        );
        recaptchaScriptElement.onload = () => {
          resolve();
        };

        recaptchaScriptElement.onerror = () => {
          const message = "Failed to load recaptcha script";
          reject(new Error(message));
        };
        this.shadow.appendChild(recaptchaScriptElement);
      });

      resourcePromises.push(recaptchaPromise);
    }

    await Promise.all(resourcePromises);
  }

  renderComponent() {
    this.reactRoot ??= createRoot(this.shadow);
    const node = this.childrenFactory?.();
    if (node) {
      this.reactRoot.render(<ReactWrapper>{node}</ReactWrapper>);
    }
  }
}

export default ProductShadowBase;
